import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useInView } from 'react-intersection-observer';
import FaceRecognitionIcon from '../../icons/FaceRecognition';
import MobileAccessIcon from '../../icons/MobileAccess';
import PinCodeIcon from '../../icons/PinCode';
import IntercomIcon from '../../icons/Intercom';
import CardAccessIcon from '../../icons/CardAccess';
import VoiceUnlockIcon from '../../icons/Voice';
import ScanQrCodeIcon from '../../icons/ScanQrCode';
import Car2Icon from '../../icons/Car2';
import Dashboard2Icon from '../../icons/Dashboard2';
import DoorLockIcon from '../../icons/DoorLock';
import styles from './styles.module.scss';
import Heading from '../../shared/heading';

const getIcon = (iconName) => {
  switch (iconName) {
    case 'face-recognition':
      return FaceRecognitionIcon;
    case 'mobile-access':
      return MobileAccessIcon;
    case 'pin-code':
      return PinCodeIcon;
    case 'video-intercom':
      return IntercomIcon;
    case 'key-card':
      return CardAccessIcon;
    case 'voice-unlock':
      return VoiceUnlockIcon;
    case 'qr-code':
      return ScanQrCodeIcon;
    case 'car':
      return Car2Icon;
    case 'dashboard':
      return Dashboard2Icon;
    case 'door-lock':
      return DoorLockIcon;
    default:
      return FaceRecognitionIcon;
  }
};

const IconsRow = ({
  theme = '',
  items = [],
  title = null,
  description = null,
  iconsLabelsVisibility = 'visible',
}) => {
  const [imageUrls, setImageUrls] = useState({});
  const [ref, inView] = useInView({
    threshold: 0.5,
    // triggerOnce: true,
  });

  useEffect(() => {
    const imagesMap = items?.reduce((acc, item) => {
      if (!item.image?.id) return acc;
      const source =
        document
          .getElementById(`icons-row-img-${item.image.id}`)
          ?.querySelector('img[data-main-image]') ||
        document.getElementById(`icons-row-img-${item.image.id}`)?.querySelector('img');
      const urls = (source?.dataset?.srcset || source?.srcset || source?.src)?.split(',');
      return {
        ...acc,
        [item.image.id]: urls?.[urls.length - 1]?.split(' ')?.[0]?.trim(),
      };
    }, {});
    setImageUrls(imagesMap);
  }, [items]);

  return (
    <section
      className={cn(styles.root, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
        [styles.inView]: inView,
        [styles.alwaysShowLabels]: iconsLabelsVisibility === 'visible',
      })}
      ref={ref}
    >
      <div className="container">
        {title && (
          <Heading className={styles.title} tag="h2" size="xl">
            {title}
          </Heading>
        )}
        {description && (
          <div className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />
        )}
        <div className={styles.iconsContainer}>
          {items.map(({ icon, label, image }, index) => {
            const Icon = getIcon(icon);
            return (
              <div
                key={index}
                className={styles.iconWrapper}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {imageUrls[image?.id] && (
                  <div className={styles.hoverMedia}>
                    <img
                      className={styles.hoverImage}
                      src={imageUrls[image.id]}
                      alt={label}
                      loading="lazy"
                    />
                  </div>
                )}
                <Icon className={styles.icon} />
                <span className={styles.label}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

IconsRow.propTypes = {
  theme: PropTypes.oneOf(['', 'light', 'dark']),
  title: PropTypes.string,
  description: PropTypes.string,
  iconsLabelsVisibility: PropTypes.oneOf(['visible', 'hover']),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      image: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      }).isRequired,
    })
  ).isRequired,
};

export default IconsRow;
